import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GhostButton from '../oracle/GhostButton';
import AurumButton from '../oracle/AurumButton';
import { COPY } from '../../contracts/copyMap';
import { CANCEL_REASONS, CANCEL_NOTE_MAX } from '../../contracts/billingApi';

// CancelMembershipModal — asks why, then lets them go.
//
// ── THE ONE RULE ────────────────────────────────────────────────────────────
//
// This modal must never feel like an obstacle. It has exactly one job beyond
// collecting the reason: make sure the tap was deliberate. It does not
// counter-offer, it does not discount, it does not bury the confirm button,
// and it does not require a reason to proceed.
//
// A member who wants out and cannot find the exit files a chargeback, and a
// chargeback costs more than the subscription. The reason is worth having;
// it is not worth trapping anyone for.
//
// So: reasons are OPTIONAL and the confirm is always live. The server accepts a
// missing reason too — see cancelSubscription.
//
// ── WHY THE REASSURANCE IS ABOVE THE BUTTONS ────────────────────────────────
//
// The single most common fear at this moment is "will I lose my scans?" They
// will not — cancelling ends the billing, keeps the account and the archive,
// and access runs to the end of the paid period. Saying so before the confirm
// turns a panicked cancel into an informed one, and some of them stay.
//
// ── VISUAL REGISTER ─────────────────────────────────────────────────────────
//
// Deliberately quieter than UnlockGateModal: no aura, no foil sweep, no motes.
// That modal is selling. This one is not, and dressing an exit in the same gold
// theatre reads as a trap. Bone type on Ash, one Aurum accent on the selected
// reason. No red anywhere — DESIGN.md forbids it even for destructive actions.

export default function CancelMembershipModal({
  open,
  onConfirm,
  onDismiss,
  submitting = false,
  error = null,
  periodEndLabel = null,
}) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');

  // Reset on each open so a previous half-filled answer never rides along into
  // a later session.
  useEffect(() => {
    if (open) {
      setReason(null);
      setNote('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      // Escape keeps the membership. The safe direction: an accidental key
      // press must never be the thing that cancels a subscription.
      if (e.key === 'Escape' && !submitting) onDismiss?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onDismiss, submitting]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian/[0.88] p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          role="dialog"
          aria-modal="true"
          aria-label={COPY.CANCEL_MODAL_HEADLINE}
          data-testid="cancel-membership-modal"
          onClick={submitting ? undefined : onDismiss}
        >
          <motion.div
            className="relative w-[520px] max-w-full max-h-[92vh] overflow-y-auto scrollbar-hide rounded-modal border border-hairline bg-obsidian-raised p-7"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.32, ease: [0.34, 1.16, 0.64, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="m-0 font-display uppercase tracking-[0.24em] text-bone-dim" style={{ fontSize: 10 }}>
              {COPY.CANCEL_MODAL_EYEBROW}
            </p>
            <h2 className="m-0 mt-2 font-display text-[26px] leading-tight text-bone">
              {COPY.CANCEL_MODAL_HEADLINE}
            </h2>
            <p className="m-0 mt-2 font-display text-[14.5px] text-bone-dim">
              {COPY.CANCEL_MODAL_BODY}
            </p>

            <div className="mt-6" role="radiogroup" aria-label={COPY.CANCEL_MODAL_REASON_TITLE}>
              <p className="m-0 mb-3 font-display uppercase tracking-[0.2em] text-bone-dim" style={{ fontSize: 11 }}>
                {COPY.CANCEL_MODAL_REASON_TITLE}
              </p>
              <div className="flex flex-col gap-2">
                {CANCEL_REASONS.map((r) => {
                  const selected = reason === r.key;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      data-testid={`cancel-reason-${r.key}`}
                      // Tapping the selected reason clears it. The list is
                      // optional, so it has to be possible to un-answer.
                      onClick={() => setReason(selected ? null : r.key)}
                      className={[
                        'w-full text-left rounded-thumb border px-4 py-2.5',
                        'font-display text-[14px] transition-colors duration-150',
                        selected
                          ? 'border-aurum/70 bg-aurum/[0.08] text-bone'
                          : 'border-hairline bg-transparent text-bone-dim hover:border-hairline-soft',
                      ].join(' ')}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="mt-5 block">
              <span className="font-display uppercase tracking-[0.2em] text-bone-dim" style={{ fontSize: 11 }}>
                {COPY.CANCEL_MODAL_NOTE_LABEL}
              </span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, CANCEL_NOTE_MAX))}
                maxLength={CANCEL_NOTE_MAX}
                rows={3}
                data-testid="cancel-note"
                placeholder={COPY.CANCEL_MODAL_NOTE_PLACEHOLDER}
                className="mt-2 w-full resize-none rounded-thumb border border-hairline bg-black/30 px-3 py-2 font-display text-[14px] text-bone placeholder:text-bone-faint focus:border-aurum/60 focus:outline-none"
              />
            </label>

            {/* Above the buttons on purpose — the fear at this moment is losing
                the archive, and answering it before the confirm is what turns a
                panicked cancel into an informed one. */}
            <p className="m-0 mt-5 font-display text-[13px] text-bone-dim">
              {periodEndLabel
                ? `${COPY.CANCEL_MODAL_KEEPS_ACCESS_PREFIX} ${periodEndLabel}. ${COPY.CANCEL_MODAL_KEEPS_DATA}`
                : COPY.CANCEL_MODAL_KEEPS_DATA}
            </p>

            {error && (
              <p className="m-0 mt-4 font-display text-[13.5px] text-bone" role="alert">
                {error}
              </p>
            )}

            <div className="mt-6 flex flex-col gap-2">
              {/* Staying is the WEIGHTED action, and it is first. Not a dark
                  pattern: the confirm below is a full-width live button with
                  plain language, not a greyed-out afterthought. */}
              <AurumButton
                onClick={onDismiss}
                disabled={submitting}
                fullWidth
                data-testid="cancel-keep"
              >
                {COPY.CANCEL_MODAL_KEEP_CTA}
              </AurumButton>

              <GhostButton
                onClick={() => onConfirm({ reason, note })}
                loading={submitting}
                fullWidth
                data-testid="cancel-confirm"
              >
                {COPY.CANCEL_MODAL_CONFIRM_CTA}
              </GhostButton>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
